// src/pages/MonEspace.js
// -----------------------------------------------------------------------------
// Page "Mon espace" – dashboard de l'ado
// Contenu adaptatif selon l'avancée dans l'application
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  getUserResult,
  getRiasecUserResult,
  getJourdaUserResult,
  getImUserResult,
  sendValuesAgreement,
  sendRiasecAgreement,
  sendJourdaAgreement,
  sendImAgreement,
} from '../api/questionnaires';
import { QUIZZES as QUIZZES_CONFIG, QUIZZES_BY_ID } from '../data/quizzes';
import PremiumModal from '../components/premium/PremiumModal';
import TivBanner from '../components/tiv/TivBanner';
import {
  valeursResultsConfig,
  riasecResultsConfig,
  imResultsConfig,
} from '../data/resultsScreenConfigs';

// Couleurs fixes du podium mystère (état zéro)
const PODIUM_COLORS = [
  { bg: "rgba(236,72,153,0.08)", bd: "rgba(236,72,153,0.15)", rc: "#ec4899", tc: "#fff" },
  { bg: "rgba(139,92,246,0.04)", bd: "rgba(139,92,246,0.08)", rc: "#a78bfa", tc: "#fff" },
  { bg: "rgba(245,158,11,0.06)", bd: "rgba(245,158,11,0.12)", rc: "#f59e0b", tc: "#1a1a2e" },
];

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─────────────────────────────────────────────
// QuizCard
// ─────────────────────────────────────────────

function QuizCard({ quiz, state, onClick }) {
  const isLocked = state === "locked";
  const isNext = state === "next";
  const isDone = state === "done-new" || state === "done-pending" || state === "done-seen";
  const isAvailable = state === "available";

  // Badge
  const badge = {
    "next":         { label: "Commencer",           cls: "bg-violet-600 text-white" },
    "done-new":     { label: "Voir tes résultats",   cls: "bg-green-500/10 text-green-400" },
    "done-pending": { label: "Voir tes résultats",   cls: "bg-green-500/10 text-green-400" },
    "done-seen":    { label: "Revoir tes résultats", cls: "bg-green-500/10 text-green-400" },
    "available":    { label: "5 min",                cls: "bg-violet-500/10 text-violet-400" },
    "locked":       { label: quiz.lockLabel || "Après Valeurs", cls: "bg-white/[0.04] text-zinc-600" },
  }[state];

  const hasRichResults = isDone && (quiz.podium || quiz.podiumInterets || quiz.podiumIntelligences || quiz.profilPhare);

  function renderMiniPodium(items) {
    return (
      <div className="flex flex-wrap gap-1">
        {items.map((item, i) => (
          <div
            key={i}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-md"
            style={{ background: item.bg, border: `1px solid ${item.bd}` }}
          >
            <div
              className="flex items-center justify-center text-[8px] font-extrabold shrink-0 rounded-[4px]"
              style={{ width: 16, height: 16, background: item.rc, color: item.tc }}
            >
              {"#" + (i + 1)}
            </div>
            <span className="text-[10px] font-semibold text-zinc-100">{item.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className={[
        "w-full flex items-center gap-3 p-3.5 rounded-2xl text-left mb-2 transition-opacity",
        isNext
          ? "bg-violet-500/[0.06] border border-violet-500/25"
          : "bg-white/[0.03] border border-white/[0.06]",
        isLocked ? "opacity-60 cursor-pointer" : isAvailable ? "opacity-75 cursor-pointer" : "cursor-pointer",
      ].join(" ")}
    >
      {/* Icône */}
      <div className={[
        "w-9 h-9 rounded-xl flex items-center justify-center text-base shrink-0",
        isNext ? "bg-violet-500/15" : isDone ? "bg-green-500/10" : "bg-white/[0.04]",
      ].join(" ")}>
        {isDone
          ? <span className="text-green-400 text-sm font-bold">✓</span>
          : quiz.emoji}
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <div className={`flex items-center gap-2 ${hasRichResults ? "mb-2" : "mb-0.5"}`}>
          <span className={`text-sm font-semibold ${isLocked ? "text-zinc-500" : "text-zinc-100"}`}>
            {quiz.label}
          </span>
          <span className={`text-[10px] font-semibold rounded-md px-2 py-0.5 ${badge.cls}`}>
            {badge.label}
          </span>
        </div>

        {/* Résultats enrichis */}
        {isDone && quiz.podium
          ? renderMiniPodium(quiz.podium.map((v, i) => ({
            label: v.name,
            bg: PODIUM_COLORS[i].bg, bd: PODIUM_COLORS[i].bd,
            rc: PODIUM_COLORS[i].rc, tc: PODIUM_COLORS[i].tc,
          })))
          : isDone && quiz.podiumInterets
            ? renderMiniPodium(quiz.podiumInterets)
            : isDone && quiz.podiumIntelligences
              ? renderMiniPodium(quiz.podiumIntelligences)
              : isDone && quiz.profilPhare
                ? (
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-[10px] font-bold text-orange-400 bg-orange-400/10 border border-orange-400/25 px-2 py-0.5 rounded-md">
                      {quiz.profilPhare.label}
                    </span>
                    {(quiz.profilsSecondaires || []).map((s, i) => (
                      <span key={i} className="text-[10px] text-zinc-500 bg-white/[0.04] border border-white/[0.08] px-2 py-0.5 rounded-md">
                        {s}
                      </span>
                    ))}
                  </div>
                )
                : (
                  <span className={`text-xs leading-relaxed ${isLocked ? "text-zinc-500" : "text-zinc-500"}`}>
                    {isDone ? quiz.doneSubtitle : quiz.subtitle}
                  </span>
                )
        }
      </div>

      {!isLocked && <span className="text-zinc-500 text-sm">›</span>}
    </button>
  );
}

// ─────────────────────────────────────────────
// Helpers podium
// ─────────────────────────────────────────────

function getValeursPodium(result) {
  if (!result) return null;
  const items = valeursResultsConfig.processResults(result);
  return items?.length >= 3 ? items.slice(0, 3) : null;
}

function getRiasecPodium(result) {
  if (!result) return null;
  const items = riasecResultsConfig.processResults(result);
  if (!items || items.length < 3) return null;
  return items.slice(0, 3).map(item => ({
    label: item.name,
    bg: hexToRgba(item.color, 0.08),
    bd: hexToRgba(item.color, 0.15),
    rc: item.color, tc: "#fff",
  }));
}

function getImPodium(result) {
  if (!result) return null;
  const items = imResultsConfig.processResults(result);
  if (!items || items.length < 3) return null;
  return items.slice(0, 3).map(item => ({
    label: item.name,
    bg: hexToRgba(item.color, 0.08),
    bd: hexToRgba(item.color, 0.15),
    rc: item.color, tc: "#fff",
  }));
}

function getJourdaProfilPhare(result) {
  if (!result) return null;
  return {
    profilPhare: { label: result.results.profile.profileType },
    profilsSecondaires: result.results.profile.adjacentProfiles,
  };
}

// ─────────────────────────────────────────────
// Libellé de section
// ─────────────────────────────────────────────

function SectionLabel({ children, className = "" }) {
  return (
    <p className={`text-[11px] font-bold text-zinc-500 uppercase tracking-widest mb-2.5 ${className}`}>
      {children}
    </p>
  );
}

// ─────────────────────────────────────────────
// Page principale
// ─────────────────────────────────────────────

export default function MonEspace() {
  const { user, updateUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const prenom = user?.firstName || user?.name || '';
  const isTerminale = user?.school_level === 'terminale';

  const [loading, setLoading] = useState(true);
  const [valeursResult, setValeursResult] = useState(null);
  const [riasecResult, setRiasecResult] = useState(null);
  const [jourdaResult, setJourdaResult] = useState(null);
  const [imResult, setImResult] = useState(null);
  const [sharedV, setSharedV] = useState(false);
  const [sharedI, setSharedI] = useState(false);
  const [sharedE, setSharedE] = useState(false);
  const [sharedInt, setSharedInt] = useState(false);
  const [sendingShare, setSendingShare] = useState(false);

  useEffect(() => {
    async function fetchResults() {
      try {
        const [v, r, j, im] = await Promise.all([
          getUserResult().catch(() => null),
          getRiasecUserResult().catch(() => null),
          getJourdaUserResult().catch(() => null),
          getImUserResult().catch(() => null),
        ]);
        setValeursResult(v);
        setRiasecResult(r);
        setJourdaResult(j);
        setImResult(im);
        // Initialiser l'état de partage depuis le serveur
        setSharedV(!!v?.share_to_parent);
        setSharedI(!!r?.share_to_parent);
        setSharedE(!!j?.share_to_parent);
        setSharedInt(!!im?.share_to_parent);
      } finally {
        setLoading(false);
      }
    }
    fetchResults();
  }, []);

  const qValeurs = !!valeursResult;
  const qInterets = !!riasecResult;
  const qEnergies = !!jourdaResult;
  const qIntelligences = !!imResult;
  const nbDone = [qValeurs, qInterets, qEnergies, qIntelligences].filter(Boolean).length;
  const allDone = nbDone === 4;

  const jourdaProfil = getJourdaProfilPhare(jourdaResult);

  function quizLock(isPremiumQuiz) {
    // if (isPremiumQuiz && !user?.is_premium) return { locked: true, lockLabel: "Premium" };
    if (isPremiumQuiz && !qValeurs) return { locked: true, lockLabel: "Après Valeurs" };
    return { locked: false, lockLabel: null };
  }
  const DYNAMIC = {
    valeurs: { subtitle: "Découvre ce qui compte pour toi", doneSubtitle: "Tes 3 valeurs clés", podium: getValeursPodium(valeursResult), done: qValeurs, ...quizLock(false) },
    interets: { subtitle: "Découvre ce qui t'attire", doneSubtitle: "Tes 3 univers préférés", podiumInterets: getRiasecPodium(riasecResult), done: qInterets, ...quizLock(true) },
    energies: { subtitle: "Découvre comment tu fonctionnes", doneSubtitle: "Ton profil d'énergie", profilPhare: jourdaProfil?.profilPhare, profilsSecondaires: jourdaProfil?.profilsSecondaires, done: qEnergies, ...quizLock(true) },
    intelligences: { subtitle: "Découvre tes intelligences dominantes", doneSubtitle: "Tes 3 intelligences dominantes", podiumIntelligences: getImPodium(imResult), done: qIntelligences, ...quizLock(true) },
  };
  const QUIZZES = QUIZZES_CONFIG.map(q => ({
    ...q,
    label: `Tes ${q.label.toLowerCase()}`,
    ...DYNAMIC[q.id],
  }));

  const suggestedOrder = ["valeurs", "interets", "energies", "intelligences"];
  let nextSuggested = null;
  for (const id of suggestedOrder) {
    const q = QUIZZES.find(x => x.id === id);
    if (q && !q.done && !q.locked) { nextSuggested = q.id; break; }
  }

  function getQuizState(quiz) {
    if (quiz.locked) return "locked";
    if (quiz.done) return "done-seen";
    if (quiz.id === nextSuggested) return "next";
    return "available";
  }

  const [showPremiumModal,   setShowPremiumModal]   = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('');
  const [premiumModalContext, setPremiumModalContext] = useState('');

  function openPremium(reason, context) {
    setPremiumModalReason(reason);
    setPremiumModalContext(context);
    setShowPremiumModal(true);
  }

  function openPremiumForQuiz(quizId) {
    const pronoun = user?.gender === 'boy' ? 'le' : 'la';
    openPremium(
      QUIZZES_BY_ID[quizId].premiumReason(pronoun),
      "Ce questionnaire est réservé aux membres Premium"
    );
  }

  const actionState = !qValeurs ? "zero" : !allDone ? "partial" : "complete";

  const coachs = [
    { id: "lux", label: "Lux", desc: "Coach en orientation", icon: "L", color: "#7c3aed", show: true, path: "/chat/lux" },
    { id: "dip", label: "Dip", desc: "Explorer les formations", icon: "D", color: "#0d9488", show: isTerminale, path: "/dip", premium: true, premiumContext: "Dip est réservé aux membres Premium", premiumReason: "explorer les formations avec Dip" },
    { id: "sup", label: "Sup", desc: "Spécialiste Parcoursup", icon: "S", color: "#7C6B4A", show: isTerminale, path: "/chat/v2/sup", premium: true, premiumContext: "Sup est réservé aux membres Premium", premiumReason: "discuter avec Sup" },
    { id: "tiv", label: "Tiv", desc: "Lettres de motivation", icon: "T", color: "#2563eb", show: isTerminale, path: "/tiv", premium: true, premiumContext: "Tiv est réservé aux membres Premium", premiumReason: "écrire ses lettres de motivation avec Tiv" },
  ].filter(a => a.show);

  // Partage parental
  const shareItems = [
    { id: "valeurs", label: "Valeurs", done: qValeurs, shared: sharedV, setShared: setSharedV, send: sendValuesAgreement },
    { id: "interets", label: "Intérêts", done: qInterets, shared: sharedI, setShared: setSharedI, send: sendRiasecAgreement },
    { id: "energies", label: "Énergies", done: qEnergies, shared: sharedE, setShared: setSharedE, send: sendJourdaAgreement },
    { id: "intelligences", label: "Intelligences", done: qIntelligences, shared: sharedInt, setShared: setSharedInt, send: sendImAgreement },
  ];
  const doneItems = shareItems.filter(x => x.done);
  const pendingShare = doneItems.filter(x => !x.shared);
  const sharedDone = doneItems.filter(x => x.shared);
  const allShared = doneItems.length > 0 && pendingShare.length === 0;
  const nextToShare = pendingShare[0] || null;

  async function handleShare(item) {
    setSendingShare(true);
    try {
      await item.send();
      item.setShared(true);
    } catch (e) {
      console.error('Erreur partage', e);
    } finally {
      setSendingShare(false);
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <span className="text-zinc-500 text-sm">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-900 text-zinc-100">
      <div className="max-w-xl mx-auto px-4 py-6 pb-10">

        {/* EN-TÊTE */}
        <h2 className="text-xl font-bold text-zinc-100 mb-5">
          {prenom ? `Salut ${prenom}` : "Mon espace"}
        </h2>

        {/* ── TIV BANNER ── */}
        {isTerminale && (
          <TivBanner
            isPremium={!!user?.is_premium}
            paidTiv={!!user?.paid_tiv}
            onOpenPremium={() => openPremium("écrire ses lettres de motivation avec Tiv", "Tiv est réservé aux membres Premium")}
          />
        )}

        {/* ── ACTION CARD ── */}

        {/* État zéro : podium mystère */}
        {actionState === "zero" && (
          <div className="rounded-2xl p-4 mb-5 border border-violet-500/10 bg-violet-500/[0.04] animate-[fadeUp_0.4s_ease]">
            <h3 className="text-base font-bold text-zinc-100 mb-3">Découvre tes 3 valeurs clés</h3>
            <div className="flex gap-2 mb-4">
              {[0, 1, 2].map(i => {
                const pc = PODIUM_COLORS[i];
                return (
                  <div
                    key={i}
                    className="flex-1 flex items-center gap-2 px-2.5 py-2 rounded-xl"
                    style={{ background: pc.bg, border: `1px solid ${pc.bd}` }}
                  >
                    <div
                      className="flex items-center justify-center text-[10px] font-extrabold shrink-0 rounded-lg"
                      style={{ width: 24, height: 24, background: pc.rc, color: pc.tc }}
                    >
                      {"#" + (i + 1)}
                    </div>
                    <span className="text-base text-zinc-600">?</span>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => navigate("/questionnaires/mes-valeurs")}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold cursor-pointer"
              style={{ background: "linear-gradient(135deg, #7c3aed, #a855f7)" }}
            >
              C'est parti, 5 min
            </button>
          </div>
        )}

        {/* État partiel : deux raccourcis */}
        {actionState === "partial" && (
          <div className="flex gap-2.5 mb-5">
            <button
              onClick={() => navigate("/chat/lux")}
              className="flex-1 flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] cursor-pointer text-left"
            >
              <div className="w-8 h-8 rounded-full bg-violet-700 text-white flex items-center justify-center text-sm font-bold shrink-0">L</div>
              <div>
                <div className="text-sm font-semibold text-zinc-100">Parler avec Lux</div>
                <div className="text-[10px] text-zinc-500">Coach en orientation</div>
              </div>
            </button>
            <button
              onClick={() => navigate("/job")}
              className="flex-1 flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] cursor-pointer text-left"
            >
              <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center text-sm shrink-0">🔍</div>
              <div>
                <div className="text-sm font-semibold text-zinc-100">Rechercher des métiers</div>
                <div className="text-[10px] text-zinc-500">450+ fiches</div>
              </div>
            </button>
          </div>
        )}

        {/* État complet : célébration */}
        {actionState === "complete" && (
          <div className="rounded-2xl p-4 mb-5 bg-green-500/[0.04] border border-green-500/10">
            <h3 className="text-base font-bold text-zinc-100 mb-3">Ton profil est complet</h3>
            <button
              onClick={() => navigate("/job")}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold cursor-pointer"
              style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)" }}
            >
              Explorer les métiers
            </button>
          </div>
        )}

        {/* ── QUESTIONNAIRES ── */}
        <SectionLabel>Mieux te connaître</SectionLabel>
        {QUIZZES.map(quiz => {
          const state = getQuizState(quiz);
          return (
            <QuizCard
              key={quiz.id}
              quiz={quiz}
              state={state}
              onClick={quiz.premium && !user?.is_premium
                ? () => openPremiumForQuiz(quiz.id)
                : () => navigate(quiz.path)
              }
            />
          );
        })}

        {/* ── TES COACHS ── */}
        <SectionLabel className="mt-5">Tes coachs</SectionLabel>
        <div className="flex gap-1.5 mb-5">
          {coachs.map(coach => (
            <button
              key={coach.id}
              onClick={() => {
                if (coach.premium && !user?.is_premium) {
                  openPremium(coach.premiumReason, coach.premiumContext);
                } else if (coach.path) {
                  navigate(coach.path);
                }
              }}
              className="flex-1 flex flex-col items-center py-3.5 px-1 rounded-xl bg-white/[0.03] border border-white/[0.06] cursor-pointer"
            >
              <div
                className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-bold mb-2"
                style={{ background: coach.color }}
              >
                {coach.icon}
              </div>
              <div className="text-sm font-semibold text-zinc-100">{coach.label}</div>
              <div className="text-[11px] text-zinc-500">{coach.desc}</div>
            </button>
          ))}
        </div>

        {/* ── EXPLORER ── */}
        <SectionLabel>Explorer</SectionLabel>
        <button
          onClick={() => navigate("/job")}
          className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-white/[0.03] border border-white/[0.06] cursor-pointer mb-5 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center text-lg shrink-0">🔍</div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-zinc-100">Rechercher des métiers</div>
            <div className="text-xs text-zinc-500 mt-0.5">450+ fiches métier avec Job</div>
          </div>
          <span className="text-zinc-500 text-sm">›</span>
        </button>

        {/* ── PARTAGE PARENTAL ── */}
        {nbDone > 0 && (
          <div className="rounded-xl p-3.5 bg-white/[0.02] border border-white/[0.04]">
            {allDone && allShared ? (
              <div className="flex items-center gap-2">
                <span className="text-amber-500 text-sm">✓</span>
                <span className="text-xs text-zinc-500">Tes parents ont reçu ton profil complet</span>
              </div>
            ) : allShared ? (
              <>
                <p className="text-xs text-zinc-500 mb-1">
                  {sharedDone.length === 1
                    ? `Tes parents ont reçu ta synthèse ${sharedDone[0].label} ✓`
                    : `Tes parents ont reçu ${sharedDone.length} synthèses ✓`}
                </p>
                <p className="text-[11px] text-zinc-600">
                  Fais un nouveau questionnaire pour avoir plus à partager.
                </p>
              </>
            ) : (
              <>
                {sharedDone.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {sharedDone.map(s => (
                      <span key={s.id} className="text-[10px] text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-md">
                        {s.label} ✓
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-zinc-500 mb-1.5">
                  {sharedDone.length === 0
                    ? `Tu as découvert ${doneItems.length === 1 ? "tes " + doneItems[0].label.toLowerCase() : doneItems.length + " dimensions"}.`
                    : `Ta synthèse ${nextToShare.label} n'a pas encore été envoyée.`}
                </p>
                <p className="text-xs text-zinc-400 leading-relaxed mb-2.5">
                  {sharedDone.length === 0
                    ? "Tes parents aimeraient sûrement voir ça. Ils recevront un résumé positif, sans scores."
                    : "Tes réponses détaillées restent confidentielles."}
                </p>
                <button
                  onClick={() => !sendingShare && nextToShare && handleShare(nextToShare)}
                  disabled={sendingShare}
                  className="w-full py-3 rounded-xl border border-amber-500/60 text-amber-500 text-sm font-medium cursor-pointer disabled:opacity-60"
                >
                  {sendingShare ? "Envoi..." : `Envoyer ma synthèse ${nextToShare?.label}`}
                </button>
                {pendingShare.length > 1 && (
                  <p className="text-[10px] text-zinc-600 text-center mt-2">
                    {`+ ${pendingShare.length - 1} autre${pendingShare.length > 2 ? "s" : ""} à envoyer`}
                  </p>
                )}
              </>
            )}
          </div>
        )}

      </div>
      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context={premiumModalContext}
        reason={premiumModalReason}
      />
    </div>
  );
}
